import React, { ChangeEvent, FormEvent } from "react";
import { Prompt, RouteComponentProps } from "react-router";
import { Link } from "react-router-dom";
import { V2 } from "../../Api";

import withTitle from "../withTitle";

interface IMessagingReminderSettingsViewState {
    isLoadingRules: boolean | string;
    isSubmitting: boolean | string;
    hasChanged: boolean;

    rules: any[];
}

interface IMessagingReminderSettingsViewProps extends RouteComponentProps<any> {

}

class MessagingReminderSettingsView extends React.Component<IMessagingReminderSettingsViewProps, IMessagingReminderSettingsViewState> {
    constructor(props: Readonly<IMessagingReminderSettingsViewProps>) {
        super(props);

        const state: IMessagingReminderSettingsViewState = {
            isLoadingRules: true,
            isSubmitting: false,
            hasChanged: false,

            rules: []
        };

        this.state = state;

        this.formInputChanged = this.formInputChanged.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.saveChanges = this.saveChanges.bind(this);
    }

    public componentDidMount() {
        this.loadRules();
    }

    public render() {
        return (
            <div className="viewer-root">
                <Prompt when={this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <h2>Reminder template rules</h2>
                <div>Rules are evaluated top-down, first matching rule is used.</div>
                &nbsp;
                <table className="table-vertical">
                    <thead>
                        <tr>
                            <th className="col-smallest">#</th>
                            <th className="col-smallest" />
                            <th>Name</th>
                            <th className="col-smallest">Conditions</th>
                            <th className="col-smallest" />
                        </tr>
                    </thead>
                    <tbody>
                        {this.state.isLoadingRules === true
                            ? <tr><td colSpan={10}><div className="spinner lds-dual-ring" /></td></tr>
                            : typeof this.state.isLoadingRules === "string"
                                ? <tr><td colSpan={10}><div className="error-text">{this.state.isLoadingRules}</div></td></tr>
                                : this.state.rules.length === 0
                                    ? <tr><td colSpan={10}>There are no rules</td></tr>
                                    : this.state.rules.map((a, i) => <tr key={a.id} className={"clickable" + (a.isDisabled ? " row-gray-text" : "")} onClick={() => this.goRule(a.id)}>
                                        <td>{a.order}</td>
                                        <td><i className={a.isFinal ? "icon-check-circle" : "icon-chain"} title={a.isFinal ? "Final rule" : "Partial rule"} /></td>
                                        <td>{a.name}{a.isDisabled ? <small> (disabled)</small> : undefined}</td>
                                        <td>{a.conditionCount}</td>
                                        <td className="col-nopad">
                                            <button type="button" className="bare icon-copy" onClick={(e) => { e.stopPropagation(); this.copyRule(a.id) }} title="Copy rule" />
                                            <button type="button" className="bare icon-chevron-up" onClick={(e) => { e.stopPropagation(); this.moveRule(a.id, "up") }} title="Move up" disabled={i === 0} />
                                            <button type="button" className="bare icon-chevron-down" onClick={(e) => { e.stopPropagation(); this.moveRule(a.id, "down") }} title="Move down" disabled={i === this.state.rules.length - 1} />
                                        </td>
                                    </tr>)}
                    </tbody>
                </table>
                &nbsp;
                {this.state.isSubmitting === true
                    ? <div className="spinner lds-dual-ring" />
                    : typeof this.state.isSubmitting === "string"
                        ? <div className="error-text">{this.state.isSubmitting}</div>
                        : null}
                <div className="action-panel-dialog">
                    <span className="spring" />
                    {this.state.hasChanged ? <button type="button" className="button text-with-icon" onClick={this.saveChanges}><i className="icon-check" /><span>Save Changes</span></button> : null}
                    <Link to="/reminders/settings/rules/new" className="button text-with-icon"><i className="icon-plus" /><span>New Rule</span></Link>
                </div>
            </div>
        );
    }

    private formInputChanged(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement> | ChangeEvent<HTMLTextAreaElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,
            [name]: value
        } as any);
    }

    private async loadRules() {
        try {
            const result = await V2.get<any>("messaging/reminders/rules");

            this.setState({
                isLoadingRules: false,
                rules: result.rules
            });
        }
        catch (e) {
            this.setState({
                isLoadingRules: e.message || "Could not load rules",
            });
        }
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isSubmitting: true
            });


            this.setState({
                hasChanged: false,
                isSubmitting: false
            }, () => this.cancelForm());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not submit"
            });
        }
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private goRule(id: string) {
        this.props.history.push(`/reminders/settings/rules/${id}`);
    }

    private moveRule(id: string, direction: "up" | "down") {
        const rules = this.state.rules;
        let rowIndex = rules.findIndex(a => a.id === id);
        if (rowIndex === -1) {
            return;
        }

        if ((direction === "up" && rowIndex === 0) || (direction === "down" && rowIndex === rules.length - 1)) {
            return;
        }

        const [row] = rules.splice(rowIndex, 1);

        rowIndex = direction === "up"
            ? rowIndex - 1
            : rowIndex + 1;

        rules.splice(rowIndex, 0, row);

        this.setState({
            hasChanged: true,
            rules
        });
    }

    private copyRule(id: string) {
        this.props.history.push("/reminders/settings/rules/new", { copyFromId: id });
    }

    private async saveChanges() {
        try {
            this.setState({
                isSubmitting: true
            });

            // Find new rule positions
            let order = 1;
            const orderChanges = [];
            for (const rule of this.state.rules) {
                if (rule.order !== order) {
                    orderChanges.push({
                        id: rule.id,
                        order
                    });
                }

                order++;
            }

            const data = {
                changes: orderChanges
            };

            await V2.patch("messaging/reminders/rules", data);

            this.setState({
                hasChanged: false,
                isSubmitting: false
            }, () => this.loadRules());
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || "Could not save changes"
            });
        }
    }
}

export default withTitle("Reminder Settings", MessagingReminderSettingsView);
